import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, LayoutGrid, Users, MessageSquare, Search, Rows3, Crosshair } from "lucide-react";
import { ConceptBar, CommandPalette, useConceptData, useCandidate, useSimulated, useHotkeys, useSelection, useVirtual, todayItems, filterRows, actionsFor, GROUP_LABEL, GROUP_ORDER, STATUS_LABEL, READY_LABEL } from "@/concepts/shared";

const D = "bold";
const OUT = { supported: "var(--lime)", not_met: "var(--coral)", unknown: "var(--amber)" };
const GCOL = { strong: "var(--lime)", possible: "var(--sky)", needs_information: "var(--amber)", low_priority: "#6F7A74", excluded: "var(--coral)" };
const PIPE = [["queued", "#4B5A53"], ["pending_review", "#6F8A7E"], ["contacted", "var(--sky)"], ["pre_screening", "var(--amber)"], ["ready_for_first_visit", "var(--lime)"]];

const Rail = ({ onSearch, active }) => (
  <nav className="flex w-16 shrink-0 flex-col items-center gap-2 border-r border-[color:var(--line)] py-4" aria-label="Primary">
    <Link to="/concepts" className="mb-4 flex h-10 w-10 items-end justify-center bg-[color:var(--lime)] pb-1 text-[18px] font-extrabold leading-none text-black" data-testid="bold-logo">a.</Link>
    {[[Zap, "Today", "today"], [LayoutGrid, "Workbench", "workbench"], [Users, "Participants", null], [MessageSquare, "Comms (planned)", null]].map(([I, l, s]) => (
      s ? <Link key={l} to={`/concepts/${D}/${s}`} title={l} aria-label={l} data-testid={`bold-nav-${s}`} className={`flex h-10 w-10 items-center justify-center rounded-lg ${active === s ? "bg-[color:var(--surface2)] text-[color:var(--lime)]" : "text-[color:var(--muted)] hover:text-white"}`}><I className="h-5 w-5" /></Link>
        : <span key={l} title={l} className="flex h-10 w-10 items-center justify-center text-[color:var(--muted)] opacity-40"><I className="h-5 w-5" /></span>
    ))}
    <button onClick={onSearch} aria-label="Search (⌘K)" className="mt-auto flex h-10 w-10 items-center justify-center rounded-lg border border-[color:var(--line)] text-[color:var(--muted)] hover:text-white" data-testid="bold-search"><Search className="h-4 w-4" /></button>
  </nav>
);

const Pill = ({ children, color }) => <span className="mono inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10.5px] uppercase tracking-wider" style={{ borderColor: color, color }}>{children}</span>;

export const BoldToday = () => {
  const { dash, studies, studyId } = useConceptData();
  const [open, setOpen] = useState(false);
  useHotkeys({ "mod+k": () => setOpen(true) });
  const items = todayItems(dash, D);
  if (!dash) return <div className="c-bold min-h-screen p-10">Loading…</div>;
  const m = dash.metrics, next = items[0];
  return (
    <div className="c-bold flex min-h-screen" data-testid="bold-today">
      <Rail onSearch={() => setOpen(true)} active="today" />
      <main className="flex-1 px-8 pb-24 pt-8">
        <p className="mono text-[11px] uppercase tracking-[0.2em] text-[color:var(--muted)]">{new Date(dash.today).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</p>
        <h1 className="mt-1 text-[52px] font-extrabold leading-[0.95] tracking-[-0.03em] md:text-[72px]">{items.length} things<br /><span className="text-[color:var(--lime)]">want you today.</span></h1>
        <div className="mt-8 grid grid-cols-12 gap-3">
          {next && (
            <Link to={next.to} className="c-rise col-span-12 flex flex-col justify-between rounded-2xl border-2 border-[color:var(--lime)] bg-[color:var(--surface)] p-6 lg:col-span-7" data-testid="bold-next-up">
              <Pill color="var(--lime)">Next up · {next.kind}</Pill>
              <div className="mt-8">
                <p className="text-[34px] font-semibold leading-tight tracking-tight">{next.title}</p>
                <p className="mt-1 text-[17px] text-[color:var(--muted)]">{next.who} · <span className="mono text-[13px]">{next.ref} · {next.study}</span> · {next.when}</p>
              </div>
              <span className="mt-6 self-start rounded-full bg-[color:var(--lime)] px-5 py-2.5 text-[14px] font-semibold text-black">{next.action} →</span>
            </Link>
          )}
          <div className="col-span-12 grid grid-cols-2 gap-3 lg:col-span-5">
            {[["Follow-ups due", m.followups_due, "var(--text)"], ["Overdue", m.overdue, "var(--coral)"], ["Awaiting review", m.awaiting_review, "var(--lime)"], ["Towards handoff", m.handoff, "var(--sky)"]].map(([l, n, c], i) => (
              <div key={l} className="c-rise rounded-2xl bg-[color:var(--surface)] p-5" style={{ animationDelay: `${60 + i * 40}ms` }} data-testid={`bold-metric-${i}`}>
                <p className="mono text-[10.5px] uppercase tracking-wider text-[color:var(--muted)]">{l}</p>
                <p className="mt-3 text-[56px] font-extrabold leading-none tracking-tight tnum" style={{ color: c }}>{n}</p>
              </div>
            ))}
          </div>
          <section className="col-span-12 rounded-2xl bg-[color:var(--surface)] p-2 lg:col-span-7" data-testid="bold-queue">
            {items.slice(1, 14).map((x) => (
              <Link key={x.id} to={x.to} className="group grid grid-cols-[4px_1fr_auto] items-center gap-4 rounded-xl px-3 py-3 hover:bg-[color:var(--surface2)]">
                <span className="h-8 w-1 rounded-full" style={{ background: ["var(--coral)", "var(--amber)", "var(--sky)", "var(--lime)", "#6F7A74"][x.urgency] }} />
                <span><span className="text-[15px] font-semibold">{x.title}</span><span className="block text-[12.5px] text-[color:var(--muted)]">{x.who} · <span className="mono">{x.ref} · {x.study}</span></span></span>
                <span className="mono text-[11px] text-[color:var(--muted)] group-hover:text-[color:var(--lime)]">{x.when} →</span>
              </Link>
            ))}
          </section>
          <section className="col-span-12 rounded-2xl bg-[color:var(--surface)] p-5 lg:col-span-5" data-testid="bold-funnels">
            <p className="mono mb-4 text-[10.5px] uppercase tracking-wider text-[color:var(--muted)]">Study pipelines</p>
            {dash.study_progress.map((s) => (
              <Link key={s.id} to={`/concepts/${D}/workbench?study=${s.id}`} className="mb-4 block hover:opacity-90">
                <div className="flex justify-between text-[14px] font-semibold"><span className="truncate pr-3">{s.name}</span><span className="mono text-[12px] text-[color:var(--lime)]">{s.pipeline.ready_for_first_visit}/{s.target}</span></div>
                <div className="mt-2 flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-[color:var(--surface2)]">
                  {PIPE.map(([k, c]) => s.pipeline[k] > 0 && <span key={k} title={`${STATUS_LABEL[k]}: ${s.pipeline[k]}`} style={{ flex: s.pipeline[k], background: c }} />)}
                </div>
              </Link>
            ))}
            <div className="mt-3 flex flex-wrap gap-3">{PIPE.map(([k, c]) => <span key={k} className="mono flex items-center gap-1 text-[10px] text-[color:var(--muted)]"><span className="h-2 w-2 rounded-full" style={{ background: c }} />{STATUS_LABEL[k]}</span>)}</div>
          </section>
        </div>
      </main>
      <CommandPalette open={open} setOpen={setOpen} dir={D} studies={studies || []} studyId={studyId} contentClass="c-bold border-[color:var(--line)]" />
      <ConceptBar dir={D} screen="today" tone="dark" />
    </div>
  );
};

const SignalBar = ({ evidence, tall }) => (
  <div className={`flex gap-1 ${tall ? "h-3" : "h-1.5"}`}>
    {evidence.map((e) => <span key={e.label} title={`${e.label}: ${e.outcome.replace("_", " ")}`} className="flex-1 rounded-sm" style={{ background: OUT[e.outcome] }} />)}
  </div>
);

const TriageCard = ({ d, over, run }) => {
  const ev = d.evaluation, p = d.participant;
  const { primary, others } = actionsFor(d);
  const st = over[p.id] || d.relationship?.status;
  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-auto p-3 xl:grid-cols-[1fr_300px]" key={p.id}>
      <article className="c-slide rounded-2xl bg-[color:var(--surface)] p-7" data-testid="bold-triage-card">
        <div className="flex flex-wrap gap-2"><Pill color={GCOL[ev.group]}>{GROUP_LABEL[ev.group]}</Pill><Pill color={ev.readiness === "ready" ? "var(--lime)" : "var(--coral)"}>{READY_LABEL[ev.readiness]}</Pill>{st && <Pill color="var(--sky)">{STATUS_LABEL[st]}</Pill>}</div>
        <h2 className="mt-5 text-[56px] font-extrabold leading-[0.95] tracking-[-0.03em]">{p.first_name}<br />{p.last_name}</h2>
        <p className="mono mt-2 text-[12px] text-[color:var(--muted)]">{p.participant_ref} · {p.age ?? "?"}y · {p.sex || "—"} · {p.area} · last contact {p.last_contact_at ? new Date(p.last_contact_at).toLocaleDateString("en-GB") : "never"}</p>
        <div className="mt-6"><SignalBar evidence={ev.checks.filter((c) => c.kind !== "desirable" && c.key !== "status").map((c) => ({ label: c.short || c.label, outcome: c.outcome }))} tall /></div>
        <div className="mt-6 grid gap-x-6 gap-y-2 md:grid-cols-2">
          {ev.checks.map((c) => (
            <div key={c.key + c.kind} className="flex gap-2.5 text-[13.5px]">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ background: OUT[c.outcome] }} />
              <span><b className="font-semibold">{c.short || c.label}</b> <span className="mono text-[10px] uppercase text-[color:var(--muted)]">{c.kind}</span><br /><span className="text-[12.5px] text-[color:var(--muted)]">{c.detail}</span></span>
            </div>
          ))}
        </div>
        {(ev.restrictions.length > 0 || ev.conflicts.length > 0) && (
          <div className="mt-6 rounded-xl border border-[color:var(--coral)]/40 bg-[color:var(--coral)]/10 p-4 text-[13px]">
            {[...ev.restrictions.map((r) => r.label), ...ev.conflicts.map((c) => c.label)].map((t) => <p key={t}>— {t}</p>)}
          </div>
        )}
      </article>
      <aside className="flex flex-col gap-2" data-testid="bold-actions">
        {primary && (
          <button onClick={() => run(p.id, primary)} className="flex items-center justify-between rounded-2xl bg-[color:var(--lime)] px-5 py-5 text-left text-[18px] font-bold text-black hover:brightness-95" data-testid="bold-primary-action">
            {primary.label}<kbd className="mono rounded border border-black/30 px-1.5 text-[11px]">A</kbd>
          </button>
        )}
        {others.map((a, i) => (
          <button key={a.key} disabled={!a.enabled} onClick={() => run(p.id, a)} data-testid={`bold-action-${a.key}`}
            className="flex items-center justify-between rounded-xl border border-[color:var(--line)] px-4 py-3 text-left text-[13.5px] hover:border-[color:var(--muted)] disabled:cursor-not-allowed disabled:opacity-40">
            <span>{a.label}{!a.enabled && <span className="block text-[11px] text-[color:var(--muted)]">{a.reason}</span>}</span><kbd className="mono text-[10.5px] text-[color:var(--muted)]">{i + 1}</kbd>
          </button>
        ))}
        <p className="mono mt-auto text-[10.5px] leading-relaxed text-[color:var(--muted)]">J/K next/previous · A primary · X skip · T table · ⌘K search</p>
      </aside>
    </div>
  );
};

export const BoldWorkbench = () => {
  const { wb, studies, studyId } = useConceptData();
  const [group, setGroup] = useState("strong");
  const [mode, setMode] = useState("triage");
  const [open, setOpen] = useState(false);
  const { over, run } = useSimulated();
  const rows = useMemo(() => filterRows(wb?.rows || [], { group }), [wb, group]);
  const sel = useSelection(rows);
  const box = useRef(null);
  const rowH = mode === "triage" ? 62 : 44;
  const v = useVirtual(rows.length, rowH, box);
  const { data: d } = useCandidate(studyId, sel.pid);
  const step = (n) => { sel.move(n); v.reveal(Math.min(rows.length - 1, Math.max(0, sel.idx + n))); };
  const others = actionsFor(d).others;
  useHotkeys({
    "mod+k": () => setOpen(true), j: () => step(1), k: () => step(-1), arrowdown: () => step(1), arrowup: () => step(-1), x: () => step(1),
    a: () => run(sel.pid, actionsFor(d).primary), t: () => setMode((m) => (m === "triage" ? "table" : "triage")),
    ...Object.fromEntries(others.map((a, i) => [String(i + 1), () => a.enabled && run(sel.pid, a)])),
  });
  if (!wb) return <div className="c-bold min-h-screen p-10">Loading…</div>;
  return (
    <div className="c-bold flex h-screen" data-testid="bold-workbench">
      <Rail onSearch={() => setOpen(true)} active="workbench" />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex flex-wrap items-end gap-4 border-b border-[color:var(--line)] px-6 pb-4 pt-5">
          <div className="mr-auto">
            <p className="mono text-[11px] text-[color:var(--muted)]">{wb.study.reference} · {wb.run?.pool_size} in pool of {wb.run?.database_size}</p>
            <h1 className="text-[30px] font-extrabold leading-tight tracking-[-0.02em]">{wb.study.name}</h1>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {GROUP_ORDER.map((g) => (
              <button key={g} onClick={() => setGroup(g)} data-testid={`bold-group-${g}`}
                className={`rounded-full px-3.5 py-1.5 text-[13px] font-semibold ${group === g ? "text-black" : "bg-[color:var(--surface)] text-[color:var(--muted)] hover:text-white"}`} style={group === g ? { background: GCOL[g] } : {}}>
                {GROUP_LABEL[g]} <span className="mono text-[11px] opacity-70">{wb.rows.filter((r) => r.group === g).length}</span>
              </button>
            ))}
          </div>
          <div className="flex rounded-full bg-[color:var(--surface)] p-1" role="tablist">
            {[["triage", Crosshair], ["table", Rows3]].map(([m, I]) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`bold-mode-${m}`} className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-[12.5px] capitalize ${mode === m ? "bg-white text-black" : "text-[color:var(--muted)]"}`}><I className="h-3.5 w-3.5" />{m}</button>
            ))}
          </div>
        </header>
        <div className="flex min-h-0 flex-1">
          <div ref={box} className={`${mode === "triage" ? "w-[300px] border-r border-[color:var(--line)]" : "flex-1"} min-h-0 shrink-0 overflow-auto`} data-testid="bold-list">
            <div style={{ height: v.total, position: "relative" }}>
              {rows.slice(v.start, v.end).map((r, i) => {
                const on = r.participant_id === sel.pid;
                const st = over[r.participant_id] || r.status;
                return (
                  <button key={r.participant_id} onClick={() => sel.select(r.participant_id)} data-testid={`bold-row-${r.participant_ref}`}
                    className={`absolute left-0 right-0 border-b border-[color:var(--line)] px-4 text-left ${on ? "bg-[color:var(--surface2)] shadow-[inset_3px_0_0_var(--lime)]" : "hover:bg-[color:var(--surface)]"}`} style={{ top: v.top + i * rowH, height: rowH }}>
                    {mode === "triage" ? (
                      <span className="flex h-full flex-col justify-center gap-1.5">
                        <span className="flex justify-between text-[14px] font-semibold"><span className="truncate">{r.name}</span><span className="mono text-[10.5px] text-[color:var(--muted)]">#{r.rank}</span></span>
                        <SignalBar evidence={r.evidence} />
                        <span className="mono text-[10px] text-[color:var(--muted)]">{st ? STATUS_LABEL[st] : r.readiness === "ready" ? "Ready" : READY_LABEL[r.readiness]}</span>
                      </span>
                    ) : (
                      <span className="grid h-full grid-cols-[50px_220px_160px_140px_150px_1fr] items-center gap-3 text-[13px]">
                        <span className="mono text-[11px] text-[color:var(--muted)]">#{r.rank}</span><span className="truncate font-semibold">{r.name} <span className="mono text-[10.5px] font-normal text-[color:var(--muted)]">{r.participant_ref}</span></span>
                        <SignalBar evidence={r.evidence} /><span style={{ color: r.readiness === "ready" ? "var(--lime)" : "var(--coral)" }}>{READY_LABEL[r.readiness]}</span>
                        <span>{st ? STATUS_LABEL[st] : "—"}</span><span className="truncate text-[color:var(--muted)]">{r.next_action}</span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          {mode === "triage" && (d ? <TriageCard d={d} over={over} run={run} /> : <div className="p-8 text-[color:var(--muted)]">{rows.length ? "Loading…" : "No candidates in this group."}</div>)}
        </div>
      </div>
      <CommandPalette open={open} setOpen={setOpen} dir={D} rows={wb.rows} studies={studies || []} studyId={studyId} contentClass="c-bold border-[color:var(--line)]" />
      <ConceptBar dir={D} screen="workbench" tone="dark" />
    </div>
  );
};
